// Undo / redo via JSON snapshots of state.trackData. Snapshots are
// cheap because the schema is plain data — JSON.stringify on a 1 MB
// track is ~2-3 ms, which is well under any user-perceivable delay
// for a tap-to-edit cycle.
//
// pushHistory is called after every committed mutation: an
// add/insert/delete/move-end/sharp-toggle. Drag-moves push a single
// snapshot on mouseup, not on every mousemove.
//
// `notify` is an optional callback the host can pass to pushHistory /
// undo / redo to refresh DOM (undo/redo button enablement, mode
// panels, etc.). Pure modules don't depend on the DOM.

import { clearSelection } from './state.js';

const HISTORY_MAX = 100;

export function pushHistory(state, notify) {
  state.history.stack = state.history.stack.slice(0, state.history.cursor + 1);
  state.history.stack.push(JSON.stringify(state.trackData));
  if (state.history.stack.length > HISTORY_MAX) {
    state.history.stack.shift();
  }
  state.history.cursor = state.history.stack.length - 1;
  notify?.();
}

export function undo(state, notify) {
  if (state.history.cursor <= 0) return false;
  state.history.cursor--;
  state.trackData = JSON.parse(state.history.stack[state.history.cursor]);
  clampActiveIndices(state);
  clearSelection(state);
  notify?.();
  return true;
}

export function redo(state, notify) {
  if (state.history.cursor >= state.history.stack.length - 1) return false;
  state.history.cursor++;
  state.trackData = JSON.parse(state.history.stack[state.history.cursor]);
  clampActiveIndices(state);
  clearSelection(state);
  notify?.();
  return true;
}

export function canUndo(state) { return state.history.cursor > 0; }
export function canRedo(state) { return state.history.cursor < state.history.stack.length - 1; }

// Lists may have shrunk under undo/redo — keep the active-entity
// indices in range so the editor doesn't dereference garbage.
function clampActiveIndices(state) {
  if (state.activeWallIdx >= (state.trackData.walls?.length ?? 0)) state.activeWallIdx = -1;
  if (state.activePatchIdx >= (state.trackData.surfacePatches?.length ?? 0)) state.activePatchIdx = -1;
  if (state.activeMarker === 'startLine' && !state.trackData.startLine) state.activeMarker = 'startFinish';
  if ((state.activeMarker === 'sector0' || state.activeMarker === 'sector1') &&
      (state.trackData.sectors?.length ?? 0) < 2) state.activeMarker = 'startFinish';
  if (state.activePitMarker && !state.trackData.pitLane) state.activePitMarker = null;
}
